/* NEW: UI_REVAMP */
import { Box, DrawerBody, DrawerFooter, Flex, Grid, Text, useRadioGroup } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { getCreditTransactionTypeApi } from '../../../api/query/setupApi';
import GoldPatternButton from '../../button/GoldPatternButton';
import DateRangeCalendarStage, { DateRangeInput } from '../../filter/DateRangeCalendarStage';
import { StatusFilterOptionCard } from '../../filter/FilterOptionCards';
import { getDefaultTransactionDateRange } from '../../history/filter/historyFilterConstants';
import { createCalendarConfirmHandler } from './dateRangeFilterUtils';
import { DesktopFilterContainer, MobileFilterContainer } from './FilterContainers';
import { useTransactionFilterState } from './useFilterState';

const useTransactionFilterContent = ({
	isMobile = false,
	selected,
	maxDate,
	childStatus,
	setChildStatus,
	childDateRange,
	setChildDateRange,
	draftDateRange,
	setDraftDateRange,
	isCalendarView,
	setIsCalendarView,
	setTicketStatus,
	onChangeDateRange,
	onClose,
}) => {
	const { getRootProps, getRadioProps } = useRadioGroup({
		name: isMobile ? 'transactionTypeMobile' : 'transactionType',
		value: childStatus,
		onChange: (value) => setChildStatus(value),
	});

	const row = getRootProps();
	const getCreditTransactionType = useQuery(
		['getCreditTransactionTypeApi'],
		getCreditTransactionTypeApi,
	);
	const resetDateRange = getDefaultTransactionDateRange({ selected });

	const handleCalendarReset = () => {
		setDraftDateRange(resetDateRange);
		setIsCalendarView(false);
	};

	const handleCalendarConfirm = createCalendarConfirmHandler({
		draftDateRange,
		selected,
		setChildDateRange,
		setDraftDateRange,
		setIsCalendarView,
	});

	const handleReset = () => {
		setChildStatus('ALL');
		setChildDateRange(resetDateRange);
		setDraftDateRange(resetDateRange);
		onChangeDateRange?.(resetDateRange[0], resetDateRange[1]);
		setTicketStatus('ALL');
		onClose();
	};

	const handleConfirm = () => {
		const selectedStartDate = childDateRange[0] || selected || new Date();
		const selectedEndDate = childDateRange[1] || selectedStartDate;
		onChangeDateRange?.(selectedStartDate, selectedEndDate);
		setTicketStatus(childStatus);
		onClose();
	};

	const content = isCalendarView ? (
		<DateRangeCalendarStage
			label='Transaction Date'
			dateRange={draftDateRange}
			onChangeDateRange={setDraftDateRange}
			maxDate={maxDate}
			isMobile={isMobile}
			clearLabel='Reset'
			isTextClearButton={!isMobile}
			onClear={handleCalendarReset}
			onConfirm={handleCalendarConfirm}
		/>
	) : (
		<>
			<Text
				fontSize={isMobile ? '14px' : '16px'}
				fontWeight='500'
				color='#111111'
				marginBottom='2px'
			>
				Transaction Date
			</Text>
			<DateRangeInput
				startDate={childDateRange[0]}
				endDate={childDateRange[1]}
				onClick={() => {
					setDraftDateRange(childDateRange);
					setIsCalendarView(true);
				}}
			/>

			<Text
				fontSize={isMobile ? '14px' : '16px'}
				fontWeight='500'
				color='#111111'
				marginTop='12px'
				marginBottom='2px'
			>
				Transaction Type
			</Text>
			<Grid templateColumns='repeat(4, 1fr)' gap='4px' {...row} textAlign='center'>
				{getCreditTransactionType.isSuccess &&
					getCreditTransactionType.data.map((value) => {
						const radio = getRadioProps({ value: value.code });
						return (
							<StatusFilterOptionCard
								key={value.description}
								{...radio}
								isSelected={childStatus === value.code}
								fontSize={isMobile ? '14px' : '16px'}
							>
								{value.description}
							</StatusFilterOptionCard>
						);
					})}
			</Grid>
		</>
	);

	const actions = isCalendarView ? null : isMobile ? (
		<Flex gap='8px' width='100%' alignItems='center'>
			<Box
				as='button'
				flex='0 0 auto'
				padding='0 18px'
				fontSize='16px'
				fontWeight='600'
				color='#FF5A00'
				lineHeight='100%'
				onClick={handleReset}
			>
				Reset
			</Box>
			<GoldPatternButton flex='1' height='52px' onClick={handleConfirm}>
				Confirm
			</GoldPatternButton>
		</Flex>
	) : (
		<Box marginTop='auto' padding='16px 8px 0'>
			<Flex justifyContent='space-between' alignItems='center'>
				<Box
					as='button'
					flex='0 0 auto'
					padding='0 16px'
					fontSize='16px'
					fontWeight='600'
					color='#FF5A00'
					lineHeight='100%'
					onClick={handleReset}
				>
					Reset
				</Box>
				<GoldPatternButton flex='1' height='52px' onClick={handleConfirm}>
					Confirm
				</GoldPatternButton>
			</Flex>
		</Box>
	);

	return { content, actions };
};

export const TransactionFilterPanel = ({
	isOpen,
	onClose,
	ticketStatus,
	setTicketStatus,
	selected,
	startDate,
	endDate,
	onChangeDateRange,
	maxDate,
}) => {
	const transactionFilterState = useTransactionFilterState({
		isOpen,
		ticketStatus,
		selected,
		startDate,
		endDate,
	});
	const { content, actions } = useTransactionFilterContent({
		selected,
		maxDate,
		setTicketStatus,
		onChangeDateRange,
		onClose,
		...transactionFilterState,
	});

	return (
		<DesktopFilterContainer
			isOpen={isOpen}
			isCalendarView={transactionFilterState.isCalendarView}
			onClose={onClose}
		>
			{content}
			{actions}
		</DesktopFilterContainer>
	);
};

export const MobileTransactionFilterSheet = ({
	isOpen,
	onClose,
	ticketStatus,
	setTicketStatus,
	selected,
	startDate,
	endDate,
	onChangeDateRange,
	maxDate,
}) => {
	const transactionFilterState = useTransactionFilterState({
		isOpen,
		ticketStatus,
		selected,
		startDate,
		endDate,
	});
	const { content, actions } = useTransactionFilterContent({
		isMobile: true,
		selected,
		maxDate,
		setTicketStatus,
		onChangeDateRange,
		onClose,
		...transactionFilterState,
	});

	return (
		<MobileFilterContainer isOpen={isOpen} onClose={onClose}>
			<DrawerBody padding='0'>{content}</DrawerBody>
			<DrawerFooter padding='12px 0 0 0'>{actions}</DrawerFooter>
		</MobileFilterContainer>
	);
};
